import { apiClient } from "../config/axios";

export type MemoryStatus = "DRAFT" | "APPROVED";

export interface Memory {
    id: string;
    question: string;
    answer: string;
    status: MemoryStatus;
    metadata: { category?: string; clientId: string; isPublic?: boolean } | null;
    createdAt: string;
    updatedAt: string;
}

export interface UpsertMemoryDto {
    question: string;
    answer: string;
    category?: string;
    clientId: string;
    isPublic?: boolean;
}

export interface UpdateMemoryDto {
    question?: string;
    answer?: string;
    category?: string;
    isPublic?: boolean;
}

export interface SearchMemoryDto {
    query: string;
    clientId: string;
    status?: MemoryStatus;
}

export interface ImportResult {
    total: number;
    successful: number;
    failed: number;
    errors?: string[];
}

export interface PaginationMeta {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface PaginatedResponse<T> {
    data: T[];
    meta: PaginationMeta;
}

export const memoryService = {
    async getByClient(
        clientId: string,
        options?: { status?: MemoryStatus; page?: number; limit?: number },
    ): Promise<PaginatedResponse<Memory>> {
        const params: Record<string, string> = {};
        if (options?.status) params.status = options.status;
        params.page = String(options?.page ?? 1);
        params.limit = String(options?.limit ?? 20);
        const res = await apiClient.get<PaginatedResponse<Memory>>(`/v1/memory/client/${clientId}`, { params });
        return res.data;
    },

    async getById(id: string): Promise<Memory> {
        const res = await apiClient.get<Memory>(`/v1/memory/${id}`);
        return res.data;
    },

    async create(data: UpsertMemoryDto): Promise<Memory> {
        const res = await apiClient.post<Memory>("/v1/memory/upsert", data);
        return res.data;
    },

    async update(id: string, data: UpdateMemoryDto): Promise<Memory> {
        const res = await apiClient.patch<Memory>(`/v1/memory/${id}`, data);
        return res.data;
    },

    async updateStatus(id: string, status: MemoryStatus): Promise<Memory> {
        const res = await apiClient.patch<Memory>(`/v1/memory/${id}/status`, { status });
        return res.data;
    },

    async delete(id: string): Promise<void> {
        await apiClient.delete(`/v1/memory/${id}`);
    },

    async bulkUpdateStatus(ids: string[], status: MemoryStatus): Promise<{ updated: number; notFound: string[] }> {
        const res = await apiClient.patch<{ updated: number; notFound: string[] }>("/v1/memory/bulk/status", { ids, status });
        return res.data;
    },

    async bulkDelete(ids: string[]): Promise<{ deleted: number; notFound: string[] }> {
        const res = await apiClient.delete<{ deleted: number; notFound: string[] }>("/v1/memory/bulk", {
            data: { ids },
        });
        return res.data;
    },

    async importExcel(clientId: string, file: File, isPublic?: boolean): Promise<ImportResult> {
        const formData = new FormData();
        formData.append("file", file);
        const params: Record<string, string> = { clientId };
        if (isPublic !== undefined) params.isPublic = String(isPublic);
        const res = await apiClient.post<ImportResult>("/v1/memory/upsert/excel", formData, {
            params,
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data;
    },

    async importCSV(clientId: string, file: File, isPublic?: boolean): Promise<ImportResult> {
        const formData = new FormData();
        formData.append("file", file);
        const params: Record<string, string> = { clientId };
        if (isPublic !== undefined) params.isPublic = String(isPublic);
        const res = await apiClient.post<ImportResult>("/v1/memory/upsert/csv", formData, {
            params,
            headers: { "Content-Type": "multipart/form-data" },
        });
        return res.data;
    },

    async search(data: SearchMemoryDto): Promise<Memory[]> {
        const res = await apiClient.post<Memory[]>("/v1/memory/search", data);
        return res.data;
    },
};
